import { isAddressEmpty, type AddressInput } from './address';
import { geocodeAddress } from './geocoding';

const GEOCODE_TIMEOUT_MS = 6000;

export interface StoredHomeLocation {
  address?: AddressInput | null;
  homeLatitude?: number | null;
  homeLongitude?: number | null;
}

export interface HomeLocationResult {
  /** Coordinates to send; null = send none (the server keeps what it has on update). */
  coords: { homeLatitude: number; homeLongitude: number } | null;
  /** Update only: drop stored coordinates (address removed or changed without a match). */
  clear: boolean;
  /** An address was given but could not be found. */
  failed: boolean;
}

function normalize(address: AddressInput | null | undefined): string {
  return [address?.street, address?.streetNumber, address?.postalCode, address?.city, address?.country]
    .map((p) => (p ?? '').trim().toLowerCase())
    .join('|');
}

function toQuery(address: AddressInput): string {
  const street = [address.street, address.streetNumber].map((p) => p?.trim()).filter(Boolean).join(' ');
  const city = [address.postalCode, address.city].map((p) => p?.trim()).filter(Boolean).join(' ');
  return [street, city, address.country?.trim()].filter(Boolean).join(', ');
}

/**
 * Geocodes an employee's home address for the planning assistant's start point. Saving never
 * fails because of it: without a match the employee is saved without coordinates.
 *
 * `previous` is the stored employee (update) or undefined (create).
 */
export async function resolveHomeLocation(
  address: AddressInput | null | undefined,
  previous?: StoredHomeLocation | null,
): Promise<HomeLocationResult> {
  const hasStoredCoords = previous?.homeLatitude != null && previous?.homeLongitude != null;

  if (!address || isAddressEmpty(address)) {
    return { coords: null, clear: !!previous && (hasStoredCoords || !isAddressEmpty(previous.address)), failed: false };
  }

  const changed = !previous || normalize(address) !== normalize(previous.address);
  if (!changed && hasStoredCoords) return { coords: null, clear: false, failed: false };

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), GEOCODE_TIMEOUT_MS);
  try {
    const [best] = await geocodeAddress(toQuery(address), controller.signal);
    if (best) {
      return { coords: { homeLatitude: best.latitude, homeLongitude: best.longitude }, clear: false, failed: false };
    }
  } catch {
    // Network error, timeout or rate limit — fall through and save without coordinates.
  } finally {
    window.clearTimeout(timer);
  }
  // why: a changed address must not keep the old coordinates; an unchanged one keeps none anyway.
  return { coords: null, clear: !!previous && changed, failed: true };
}
